import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { axios } from '../../lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';

const paymentColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'green';
  if (s === 'failed') return 'red';
  return 'gold';
};

const paymentLabel = (row) => {
  const stored = (row?.paymentStatus || row?.subscription?.paymentStatus || '')
    .toLowerCase();
  if (stored === 'paid' || stored === 'unpaid' || stored === 'failed') {
    return stored;
  }
  const s = (row?.subscription?.status || '').toLowerCase();
  if (s === 'active' || s === 'charged') return 'paid';
  if (s === 'pending' || s === 'halted') return 'failed';
  return 'unpaid';
};

const statusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'active' || s === 'charged') return 'green';
  if (s === 'created') return 'blue';
  if (s === 'cancelled' || s === 'canceled' || s === 'expired') return 'red';
  if (s === 'paused' || s === 'halted') return 'orange';
  return 'default';
};

const Subscription = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [paymentFilter, setPaymentFilter] = useState('all');

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/razorpay/get-all-users-subscriptions');
      const rows = response?.data || response?.subscriptions || [];
      setSubscriptions(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subscriptions.filter((row) => {
      const sub = row?.subscription || {};
      const plan = row?.plan || {};
      const user = sub.userId || {};
      const status = (sub.status || '').toLowerCase();
      const payment = paymentLabel(row);
      if (statusFilter === 'open') {
        if (status === 'cancelled' || status === 'canceled') return false;
      } else if (statusFilter !== 'all' && status !== statusFilter) {
        return false;
      }
      if (paymentFilter !== 'all' && payment !== paymentFilter) return false;
      if (!q) return true;
      const hay = [
        sub.razorpaySubscriptionId,
        user.full_name,
        user.email,
        user.mobile_number,
        plan.name,
        plan.tier,
        sub.status,
        paymentLabel(row),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [subscriptions, search, statusFilter, paymentFilter]);

  const cancelSubscription = async (record) => {
    const id =
      record?.subscription?._id ||
      record?.subscription?.razorpaySubscriptionId;
    if (!id) {
      message.error('Missing subscription id');
      return;
    }
    setCancellingId(id);
    try {
      await axios.post('/razorpay/cancel-subscription', { id });
      message.success('Subscription cancelled');
      await fetchSubscriptions();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    } finally {
      setCancellingId(null);
    }
  };

  const columns = [
    {
      title: 'Subscription ID',
      dataIndex: ['subscription', 'razorpaySubscriptionId'],
      key: 'razorpaySubscriptionId',
      ellipsis: true,
      render: (id) => id || '—',
    },
    {
      title: 'Merchant',
      key: 'user',
      render: (_, record) => {
        const user = record?.subscription?.userId || {};
        return (
          <Space direction="vertical" size={0}>
            <strong>{user.full_name || '—'}</strong>
            <span style={{ color: colors.mutedSoft, fontSize: 12 }}>
              {user.email || user.mobile_number || ''}
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => {
        const plan = record?.plan || {};
        return (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <span>{plan.name || '—'}</span>
              {plan.tier && <Tag color="blue">{plan.tier}</Tag>}
            </Space>
            <span style={{ color: colors.mutedSoft, fontSize: 12 }}>
              {plan.price != null
                ? `₹${plan.price} / ${plan.validity || 'month'}`
                : ''}
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (_, record) => {
        const plan = record?.plan || {};
        if (!plan?.name) return '—';
        const lim = (v) => (v == null ? '—' : v < 0 ? 'Unlimited' : v);
        return (
          <span style={{ fontSize: 12, color: colors.muted }}>
            C:{lim(plan.maxActiveCoupons)} · S:{lim(plan.maxShops)} · HH:
            {lim(plan.happyHoursPerMonth)} · LD:{lim(plan.luckyDrawsPerMonth)}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: ['subscription', 'status'],
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={statusColor(status)}>
          {(status || 'unknown').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Payment',
      key: 'paymentStatus',
      width: 120,
      render: (_, record) => {
        const pay = paymentLabel(record);
        return (
          <Tag color={paymentColor(pay)}>{pay.toUpperCase()}</Tag>
        );
      },
    },
    {
      title: 'Created',
      key: 'created',
      render: (_, record) => {
        const d =
          record?.subscription?.createdAt ||
          record?.subscription?.created_at;
        return d ? new Date(d).toLocaleString() : '—';
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => {
        const status = (record?.subscription?.status || '').toLowerCase();
        const done = status === 'cancelled' || status === 'canceled';
        const id =
          record?.subscription?._id ||
          record?.subscription?.razorpaySubscriptionId;
        return (
          <Popconfirm
            title="Cancel this subscription?"
            description="Stops Razorpay billing and frees the merchant to subscribe again."
            okText="Cancel subscription"
            okButtonProps={{ danger: true }}
            disabled={done}
            onConfirm={() => cancelSubscription(record)}
          >
            <Button
              danger
              size="small"
              disabled={done}
              loading={cancellingId === id}
            >
              {done ? 'Cancelled' : 'Cancel'}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <PageShell
      title="Subscription Management"
      description="View merchant subscriptions, plan tiers, and billing status. Cancel stops Razorpay auto-debit."
      actions={
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={fetchSubscriptions}
          loading={loading}
        >
          Refresh
        </Button>
      }
    >
      <Card
        style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}
        styles={{ body: { paddingTop: 16 } }}
      >
        <div className="row row_searchbar" style={{ marginTop: 0, marginBottom: 16 }}>
          <Input.Search
            placeholder="Search merchant, email, plan, or subscription id"
            allowClear
            onSearch={setSearch}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 380 }}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: 'open', label: 'Hide cancelled' },
              { value: 'all', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              { value: 'charged', label: 'Charged' },
              { value: 'created', label: 'Created' },
              { value: 'cancelled', label: 'Cancelled' },
              { value: 'paused', label: 'Paused' },
            ]}
          />
          <Select
            value={paymentFilter}
            onChange={setPaymentFilter}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: 'All payments' },
              { value: 'unpaid', label: 'Unpaid' },
              { value: 'paid', label: 'Paid' },
              { value: 'failed', label: 'Failed' },
            ]}
          />
        </div>

        <Table
          rowKey={(record) =>
            record?.subscription?._id ||
            record?.subscription?.razorpaySubscriptionId ||
            `${record?.plan?._id || 'plan'}-${record?.subscription?.userId?._id || 'user'}`
          }
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) =>
              `${total} subscription${total === 1 ? '' : 's'}`,
          }}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <Empty description="No subscriptions found" style={{ padding: 24 }} />
            ),
          }}
        />
      </Card>
    </PageShell>
  );
};

export default Subscription;
